import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import SeloSituacao from '@/components/feature/SeloSituacao';
import MenuAcoesFolha from '@/components/feature/MenuAcoesFolha';
import type { StatusInscricao } from '@/components/feature/SeloSituacao';
import { formatarDataCurta, formatarReais, formatarDinheiro } from '../utils';
import AbaDados from './AbaDados';
import AbaFicha from './AbaFicha';
import AbaFinanceiro from './AbaFinanceiro';
import FormRegistrarPagamento, { type PagamentoPayload } from './FormRegistrarPagamento';
import FormConcederAjuste, { type AjustePayload } from './FormConcederAjuste';
import FormEstornarPagamento from './FormEstornarPagamento';
import ModalConfirmarCancelar from './ModalConfirmarCancelar';
import FolhaCancelarInscricao from './FolhaCancelarInscricao';
import FolhaDarVaga from './FolhaDarVaga';
import FolhaConvite from './FolhaConvite';
import { podeGerarConvite, motivoConviteIndisponivel } from '../utils';
import type {
  InscritoGerenciar,
  SaldoInscricao,
  FichaItem,
  ItemFinanceiro,
  ResultadoCancelamento,
} from '../types';
import type { AvisoVagas, ContextoVagas } from '../vagas';

const ABAS = ['Ficha', 'Financeiro', 'Dados'] as const;
type Aba = (typeof ABAS)[number];

type ModalAberto =
  | 'nenhum'
  | 'pagamento'
  | 'ajuste'
  | 'estornar'
  | 'cancelar'
  | 'cancelar-vagas'
  | 'dar-vaga';

interface Props {
  inscrito: InscritoGerenciar;
  usuarioId: string;
  eventoId: number;
  eventoNome: string;
  eventoInicio: string;
  eventoLocal?: string | null;
  eventoCapa?: string | null;
  vendeIngressos: boolean;
  /** Contexto de vagas/suplentes do grupo desta pessoa. */
  vagas?: ContextoVagas;
  /** Avisa a lista (toast) depois de mexer em vaga/suplente. */
  aoAcaoVagas: (aviso: AvisoVagas) => void;
  aoFechar: () => void;
  aoMudouStatus: (inscricaoId: number, status: StatusInscricao) => void;
  aoMudouFinanceiro: (inscricaoId: number) => void;
}

interface PagamentoLinha {
  id: number;
  metodo: string;
  valor_centavos: number;
  registrado_por_id: string | null;
  observacao: string | null;
  pago_em: string | null;
  criado_em: string;
}

interface AjusteLinha {
  id: number;
  tipo: 'desconto' | 'isencao';
  valor_centavos: number;
  motivo_interno: string | null;
  concedido_por_id: string;
  criado_em: string;
}

export default function PainelInscricao({
  inscrito,
  usuarioId,
  eventoId,
  eventoNome,
  eventoInicio,
  eventoLocal,
  eventoCapa,
  vendeIngressos,
  vagas,
  aoAcaoVagas,
  aoFechar,
  aoMudouStatus,
  aoMudouFinanceiro,
}: Props) {
  const [aba, setAba] = useState<Aba>('Dados');
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [saldo, setSaldo] = useState<SaldoInscricao | null>(null);
  const [ficha, setFicha] = useState<FichaItem[]>([]);
  const [temFormulario, setTemFormulario] = useState(false);
  const [itens, setItens] = useState<ItemFinanceiro[]>([]);
  const [mesmaPessoa, setMesmaPessoa] = useState(false);

  const [modal, setModal] = useState<ModalAberto>('nenhum');
  const [menuAberto, setMenuAberto] = useState(false);
  const botaoMenuRef = useRef<HTMLButtonElement>(null);
  const [salvando, setSalvando] = useState(false);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [pagamentoEstornarId, setPagamentoEstornarId] = useState<number | null>(null);
  const [conviteAberto, setConviteAberto] = useState(false);

  // Trocou de pessoa: o menu do "⋮" não pode ficar aberto com as ações do anterior.
  useEffect(() => {
    setMenuAberto(false);
  }, [inscrito.inscricao_id]);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setCarregando(true);
      setErro(null);
      try {
        const [resSaldo, resFicha, resForm, resPags, resAjs, resPart] = await Promise.all([
          supabase
            .from('vw_saldo_inscricao')
            .select(
              'contratado_centavos, acrescimos_centavos, descontos_isencoes_centavos, pago_centavos, saldo_centavos',
            )
            .eq('inscricao_id', inscrito.inscricao_id)
            .maybeSingle(),
          supabase
            .from('vw_respostas_inscricao')
            .select('ordem, enunciado, tipo, resposta_texto')
            .eq('inscricao_id', inscrito.inscricao_id)
            .order('ordem', { ascending: true }),
          supabase
            .from('formularios')
            .select('id')
            .eq('modalidade_id', inscrito.modalidade_id)
            .eq('ativo', true)
            .limit(1),
          supabase
            .from('pagamentos')
            .select(
              'id, metodo, valor_centavos, registrado_por_id, observacao, pago_em, criado_em',
            )
            .eq('inscricao_id', inscrito.inscricao_id)
            .eq('status', 'aprovado'),
          supabase
            .from('ajustes_financeiros')
            .select('id, tipo, valor_centavos, motivo_interno, concedido_por_id, criado_em')
            .eq('inscricao_id', inscrito.inscricao_id)
            .in('tipo', ['desconto', 'isencao']),
          supabase
            .from('participantes')
            .select('usuario_id')
            .eq('id', inscrito.participante_id)
            .maybeSingle(),
        ]);

        if (resSaldo.error) throw resSaldo.error;
        if (resFicha.error) throw resFicha.error;
        if (resForm.error) throw resForm.error;
        if (resPags.error) throw resPags.error;
        if (resAjs.error) throw resAjs.error;
        if (resPart.error) throw resPart.error;

        const participanteUsuarioId =
          (resPart.data as { usuario_id: string | null } | null)?.usuario_id ?? null;
        const mesmaPessoaLocal =
          !!participanteUsuarioId && participanteUsuarioId === inscrito.responsavel_id;

        const pags = (resPags.data as PagamentoLinha[]) || [];
        const ajs = (resAjs.data as AjusteLinha[]) || [];

        const ids = new Set<string>();
        pags.forEach((p) => p.registrado_por_id && ids.add(p.registrado_por_id));
        ajs.forEach((a) => a.concedido_por_id && ids.add(a.concedido_por_id));

        const nomes = new Map<string, string>();
        if (ids.size > 0) {
          const { data: usuariosData } = await supabase
            .from('usuarios')
            .select('id, nome')
            .in('id', [...ids]);
          ((usuariosData as { id: string; nome: string }[]) || []).forEach((u) =>
            nomes.set(u.id, u.nome),
          );
        }

        const lista: ItemFinanceiro[] = [];
        pags.forEach((p) => {
          lista.push({
            chave: `pag-${p.id}`,
            id: p.id,
            tipo: 'pagamento',
            data: p.pago_em ?? p.criado_em,
            valor_centavos: p.valor_centavos,
            metodo: (p.metodo as ItemFinanceiro['metodo']) ?? null,
            nome: p.registrado_por_id ? (nomes.get(p.registrado_por_id) ?? null) : null,
            motivo_interno: p.observacao ?? null,
            ajuste_tipo: null,
          });
        });
        ajs.forEach((a) => {
          lista.push({
            chave: `aj-${a.id}`,
            id: a.id,
            tipo: 'ajuste',
            data: a.criado_em,
            valor_centavos: a.valor_centavos,
            metodo: null,
            nome: nomes.get(a.concedido_por_id) ?? null,
            motivo_interno: a.motivo_interno ?? null,
            ajuste_tipo: a.tipo,
          });
        });
        lista.sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());

        if (ativo) {
          setSaldo((resSaldo.data as SaldoInscricao) ?? null);
          setFicha((resFicha.data as FichaItem[]) || []);
          setTemFormulario(((resForm.data as unknown[]) || []).length > 0);
          setItens(lista);
          setMesmaPessoa(mesmaPessoaLocal);
        }
      } catch (e) {
        if (ativo) {
          setErro(
            mensagemAmigavel(e, 'Não foi possível carregar os dados.'),
          );
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [
    inscrito.inscricao_id,
    inscrito.modalidade_id,
    inscrito.participante_id,
    inscrito.responsavel_id,
    reload,
  ]);

  async function registrarPagamento(payload: PagamentoPayload) {
    setSalvando(true);
    setErroAcao(null);
    setSucesso(null);
    try {
      const { data, error } = await supabase.rpc('registrar_pagamento', {
        p_inscricao: inscrito.inscricao_id,
        p_valor_reais: payload.valor_centavos / 100,
        p_metodo: payload.metodo,
        p_observacao: payload.observacao,
        p_pago_em: payload.pago_em,
      });
      if (error) throw error;
      setModal('nenhum');
      aoMudouFinanceiro(inscrito.inscricao_id);
      setReload((r) => r + 1);

      const res = data as {
        pagamento_id: number;
        pago_agora: number;
        total_pago: number;
        ainda_falta: number;
        situacao: string;
      } | null;
      if (res) {
        const falta = Number(res.ainda_falta);
        if (falta <= 0) {
          setSucesso('QUITADO');
        } else {
          setSucesso(
            `Recebido ${formatarReais(res.pago_agora)} · falta ${formatarReais(falta)}`,
          );
        }
      }
    } catch (e) {
      setErroAcao(
        mensagemAmigavel(e, 'Não foi possível registrar o pagamento.'),
      );
    } finally {
      setSalvando(false);
    }
  }

  async function estornarPagamento(motivo: string) {
    setSalvando(true);
    setErroAcao(null);
    setSucesso(null);
    try {
      const { error } = await supabase.rpc('estornar_pagamento', {
        p_pagamento: pagamentoEstornarId,
        p_motivo: motivo,
      });
      if (error) throw error;
      setModal('nenhum');
      setPagamentoEstornarId(null);
      aoMudouFinanceiro(inscrito.inscricao_id);
      setReload((r) => r + 1);
    } catch (e) {
      setErroAcao(
        mensagemAmigavel(e, 'Não foi possível estornar o pagamento.'),
      );
    } finally {
      setSalvando(false);
    }
  }

  async function concederAjuste(payload: AjustePayload) {
    setSalvando(true);
    setErroAcao(null);
    try {
      const { error } = await supabase.from('ajustes_financeiros').insert({
        inscricao_id: inscrito.inscricao_id,
        tipo: payload.tipo,
        valor_centavos: payload.valor_centavos,
        motivo_interno: payload.motivo_interno,
        concedido_por_id: usuarioId,
      });
      if (error) throw error;
      setModal('nenhum');
      aoMudouFinanceiro(inscrito.inscricao_id);
      setReload((r) => r + 1);
    } catch (e) {
      setErroAcao(
        mensagemAmigavel(e, 'Não foi possível conceder o ajuste.'),
      );
    } finally {
      setSalvando(false);
    }
  }

  async function mudarStatus(novoStatus: StatusInscricao) {
    setSalvando(true);
    setErroAcao(null);
    try {
      const patch: Record<string, unknown> = { status: novoStatus };
      if (novoStatus === 'confirmada') patch.confirmada_em = new Date().toISOString();
      if (novoStatus === 'cancelada') patch.cancelada_em = new Date().toISOString();
      const { error } = await supabase
        .from('inscricoes')
        .update(patch)
        .eq('id', inscrito.inscricao_id);
      if (error) throw error;
      setModal('nenhum');
      aoMudouStatus(inscrito.inscricao_id, novoStatus);
    } catch (e) {
      setErroAcao(
        mensagemAmigavel(e, 'Não foi possível atualizar a situação.'),
      );
    } finally {
      setSalvando(false);
    }
  }

  const cancelada = inscrito.status === 'cancelada';
  const vagasOn = vagas?.temVagas === true;
  const ehSuplente = vagas?.ehSuplente === true;

  async function cancelarComMotivo(motivo: string, detalhe: string | null) {
    setSalvando(true);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('cancelar_inscricao', {
        p_inscricao: inscrito.inscricao_id,
        p_motivo: motivo,
        p_detalhe: detalhe,
      });
      if (error) throw error;
      const res = data as ResultadoCancelamento | null;
      if (!res?.ok) {
        setErroAcao(res?.erro ?? 'Não foi possível cancelar a inscrição.');
        return;
      }
      setModal('nenhum');
      aoMudouStatus(inscrito.inscricao_id, 'cancelada');
      setReload((r) => r + 1);

      if (res.subiu) {
        aoAcaoVagas({
          texto: `Inscrição cancelada. ${res.subiu.nome} entrou na vaga.`,
        });
      } else if (res.proximo_aguardando_aprovacao) {
        aoAcaoVagas({
          texto: `Inscrição cancelada. ${res.proximo_aguardando_aprovacao.nome} está aguardando sua aprovação.`,
          inscricaoId: res.proximo_aguardando_aprovacao.inscricao_id,
          acaoRotulo: 'Dar a vaga',
        });
      } else {
        aoAcaoVagas({ texto: 'Inscrição cancelada.' });
      }
    } catch (e) {
      setErroAcao(mensagemAmigavel(e, 'Não foi possível cancelar a inscrição.'));
    } finally {
      setSalvando(false);
    }
  }

  async function darVaga(motivo: string | null) {
    setSalvando(true);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('dar_vaga_ao_suplente', {
        p_inscricao: inscrito.inscricao_id,
        p_motivo: motivo,
      });
      if (error) throw error;
      const res = data as { ok: boolean; erro?: string } | null;
      if (!res?.ok) {
        setErroAcao(res?.erro ?? 'Não foi possível dar a vaga.');
        return;
      }
      setModal('nenhum');
      setReload((r) => r + 1);
      aoAcaoVagas({
        texto: `${inscrito.participante} saiu dos suplentes e agora tem vaga.`,
      });
    } catch (e) {
      setErroAcao(mensagemAmigavel(e, 'Não foi possível dar a vaga.'));
    } finally {
      setSalvando(false);
    }
  }

  async function moverParaSuplente() {
    setSalvando(true);
    setErroAcao(null);
    setMenuAberto(false);
    try {
      const { data, error } = await supabase.rpc('mover_para_suplente', {
        p_inscricao: inscrito.inscricao_id,
        p_motivo: null,
      });
      if (error) throw error;
      const res = data as { ok: boolean; erro?: string } | null;
      if (!res?.ok) {
        setErroAcao(res?.erro ?? 'Não foi possível mover para suplente.');
        return;
      }
      setReload((r) => r + 1);
      aoAcaoVagas({ texto: `${inscrito.participante} foi para a fila de suplentes.` });
    } catch (e) {
      setErroAcao(mensagemAmigavel(e, 'Não foi possível mover para suplente.'));
    } finally {
      setSalvando(false);
    }
  }

  async function desfazerCancelamento() {
    setSalvando(true);
    setErroAcao(null);
    setMenuAberto(false);
    try {
      const { data, error } = await supabase.rpc('desfazer_cancelamento', {
        p_inscricao: inscrito.inscricao_id,
      });
      if (error) throw error;
      const res = data as { ok: boolean; erro?: string; mensagem?: string } | null;
      if (!res?.ok) {
        setErroAcao(res?.erro ?? 'Não foi possível desfazer o cancelamento.');
        return;
      }
      setReload((r) => r + 1);
      aoAcaoVagas({ texto: res.mensagem ?? 'Cancelamento desfeito.' });
    } catch (e) {
      setErroAcao(mensagemAmigavel(e, 'Não foi possível desfazer o cancelamento.'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="animate-painel-fade absolute inset-0 bg-black/30" onClick={aoFechar} />

      <div className="animate-painel-entrar absolute right-0 top-0 flex h-full w-full flex-col bg-white md:w-[480px]">
        {/* Capa desfocada no topo */}
        <div className="relative shrink-0 overflow-hidden">
          {eventoCapa ? (
            <img
              src={eventoCapa}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full object-cover object-center"
              style={{ filter: 'blur(20px)', transform: 'scale(1.1)' }}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-400" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60" />
          <div className="relative z-10 px-5 pb-3 pt-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="truncate text-[20px] font-bold leading-tight text-white">
                  {inscrito.participante}
                </h2>
                <p className="mt-1 text-[13px] text-white/85">
                  {inscrito.modalidade}
                  {inscrito.grupo ? ` · ${inscrito.grupo}` : ''} ·{' '}
                  {formatarDataCurta(inscrito.criado_em)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {!carregando && !erro && (
                  <button
                    type="button"
                    onClick={() => {
                      if (podeGerarConvite(inscrito.status)) setConviteAberto(true);
                    }}
                    disabled={!podeGerarConvite(inscrito.status)}
                    aria-label={
                      podeGerarConvite(inscrito.status)
                        ? 'Gerar convite'
                        : motivoConviteIndisponivel(inscrito.status)
                    }
                    title={
                      podeGerarConvite(inscrito.status)
                        ? 'Gerar convite'
                        : motivoConviteIndisponivel(inscrito.status)
                    }
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/30 ${
                      podeGerarConvite(inscrito.status) ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                    }`}
                  >
                    <i className="ri-qr-code-line text-2xl" />
                  </button>
                )}
                {!carregando && !erro && (!cancelada || vagasOn) && (
                  <div className="relative">
                    <button
                      type="button"
                      ref={botaoMenuRef}
                      onClick={() => setMenuAberto((v) => !v)}
                      aria-label="Mais ações"
                      aria-expanded={menuAberto}
                      aria-haspopup="menu"
                      className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                    >
                      <i className="ri-more-2-fill text-2xl" />
                    </button>
                    {menuAberto && (
                      <MenuAcoesFolha
                        aberto={menuAberto}
                        aoFechar={() => setMenuAberto(false)}
                        titulo={inscrito.participante}
                        ancora={botaoMenuRef}
                      >
                        {vagasOn ? (
                          cancelada ? (
                            <button
                              type="button"
                              onClick={desfazerCancelamento}
                              disabled={salvando}
                              className="flex min-h-[48px] w-full cursor-pointer items-center gap-3 px-4 text-left text-[15px] font-medium text-foreground-900 transition-colors hover:bg-background-50"
                            >
                              <span className="flex h-6 w-6 items-center justify-center text-primary-600">
                                <i className="ri-arrow-go-back-line text-lg" />
                              </span>
                              Desfazer cancelamento
                            </button>
                          ) : ehSuplente ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuAberto(false);
                                  setErroAcao(null);
                                  setModal('dar-vaga');
                                }}
                                disabled={salvando}
                                className="flex min-h-[48px] w-full cursor-pointer items-center gap-3 px-4 text-left text-[15px] font-medium text-foreground-900 transition-colors hover:bg-background-50"
                              >
                                <span className="flex h-6 w-6 items-center justify-center text-success-600">
                                  <i className="ri-user-add-line text-lg" />
                                </span>
                                Dar a vaga a esta pessoa
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuAberto(false);
                                  setErroAcao(null);
                                  setModal('cancelar-vagas');
                                }}
                                disabled={salvando}
                                className="flex min-h-[48px] w-full cursor-pointer items-center gap-3 px-4 text-left text-[15px] font-medium text-danger-500 transition-colors hover:bg-background-50"
                              >
                                <span className="flex h-6 w-6 items-center justify-center">
                                  <i className="ri-close-circle-line text-lg" />
                                </span>
                                Cancelar inscrição
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={moverParaSuplente}
                                disabled={salvando}
                                className="flex min-h-[48px] w-full cursor-pointer items-center gap-3 px-4 text-left text-[15px] font-medium text-foreground-900 transition-colors hover:bg-background-50"
                              >
                                <span className="flex h-6 w-6 items-center justify-center text-secondary-500">
                                  <i className="ri-time-line text-lg" />
                                </span>
                                Mover para suplente
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuAberto(false);
                                  setErroAcao(null);
                                  setModal('cancelar-vagas');
                                }}
                                disabled={salvando}
                                className="flex min-h-[48px] w-full cursor-pointer items-center gap-3 px-4 text-left text-[15px] font-medium text-danger-500 transition-colors hover:bg-background-50"
                              >
                                <span className="flex h-6 w-6 items-center justify-center">
                                  <i className="ri-close-circle-line text-lg" />
                                </span>
                                Cancelar inscrição
                              </button>
                            </>
                          )
                        ) : (
                          <>
                            {inscrito.status === 'pendente' && (
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuAberto(false);
                                  mudarStatus('confirmada');
                                }}
                                disabled={salvando}
                                className="flex min-h-[48px] w-full cursor-pointer items-center gap-3 px-4 text-left text-[15px] font-medium text-foreground-900 transition-colors hover:bg-background-50"
                              >
                                <span className="flex h-6 w-6 items-center justify-center text-success-600">
                                  <i className="ri-check-line text-lg" />
                                </span>
                                Confirmar {vendeIngressos ? 'ingresso' : 'inscrição'}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setMenuAberto(false);
                                setModal('cancelar');
                              }}
                              disabled={salvando}
                              className="flex min-h-[48px] w-full cursor-pointer items-center gap-3 px-4 text-left text-[15px] font-medium text-danger-500 transition-colors hover:bg-background-50"
                            >
                              <span className="flex h-6 w-6 items-center justify-center">
                                <i className="ri-close-circle-line text-lg" />
                              </span>
                              Cancelar {vendeIngressos ? 'ingresso' : 'inscrição'}
                            </button>
                            {inscrito.status !== 'lista_espera' && (
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuAberto(false);
                                  mudarStatus('lista_espera');
                                }}
                                disabled={salvando}
                                className="flex min-h-[48px] w-full cursor-pointer items-center gap-3 px-4 text-left text-[15px] font-medium text-foreground-900 transition-colors hover:bg-background-50"
                              >
                                <span className="flex h-6 w-6 items-center justify-center text-secondary-500">
                                  <i className="ri-time-line text-lg" />
                                </span>
                                Mover para lista de espera
                              </button>
                            )}
                          </>
                        )}
                      </MenuAcoesFolha>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={aoFechar}
                  aria-label="Voltar"
                  className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                >
                  <i className="ri-arrow-left-line text-2xl md:hidden" />
                  <i className="ri-close-line hidden text-2xl md:block" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Situação + saldo devido */}
        <div className="border-b border-background-100 bg-white px-5 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SeloSituacao status={inscrito.status} />
            <div className="flex items-center gap-3">
              {saldo && saldo.saldo_centavos > 0 && (
                <span className="text-[14px] font-semibold text-amber-700">
                  deve {formatarDinheiro(saldo.saldo_centavos)}
                </span>
              )}
              <button
                type="button"
                onClick={() =>
                  navigate(`/gerenciar/${eventoId}/ficha/${inscrito.inscricao_id}/imprimir`)
                }
                className="flex min-h-[34px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-secondary-300 bg-white px-3 text-[12px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
              >
                <i className="ri-printer-line text-sm" />
                Imprimir ficha
              </button>
            </div>
          </div>
        </div>

        {/* Sub-abas em pílulas */}
        <div className="border-b border-background-100 bg-white px-5 py-3">
          <div className="flex gap-1 rounded-full bg-background-100 p-1">
            {ABAS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAba(a)}
                className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-[14px] font-semibold transition-colors ${
                  aba === a
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {erroAcao && modal === 'nenhum' && (
            <div className="mb-3 rounded-2xl bg-danger-50 px-4 py-3 text-[14px] text-danger-700">
              {erroAcao}
            </div>
          )}
          {sucesso && (
            <div className="mb-3 rounded-2xl bg-success-50 px-4 py-3 text-[14px] font-medium text-success-800">
              {sucesso}
            </div>
          )}
          {carregando ? (
            <div className="space-y-4">
              <div className="h-5 w-2/3 animate-pulse rounded bg-secondary-100" />
              <div className="h-4 w-full animate-pulse rounded bg-secondary-100" />
              <div className="h-4 w-4/5 animate-pulse rounded bg-secondary-100" />
              <div className="h-4 w-3/5 animate-pulse rounded bg-secondary-100" />
            </div>
          ) : erro ? (
            <div className="py-6 text-center">
              <p className="text-[15px] text-foreground-800">{erro}</p>
              <button
                type="button"
                onClick={() => setReload((r) => r + 1)}
                className="mt-4 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                Tentar de novo
              </button>
            </div>
          ) : (
            <>
              {aba === 'Dados' && (
                <AbaDados
                  inscrito={inscrito}
                  eventoInicio={eventoInicio}
                  mesmaPessoa={mesmaPessoa}
                />
              )}
              {aba === 'Ficha' && <AbaFicha ficha={ficha} temFormulario={temFormulario} />}
              {aba === 'Financeiro' && saldo && (
                <AbaFinanceiro
                  saldo={saldo}
                  itens={itens}
                  aoRegistrarPagamento={() => {
                    setErroAcao(null);
                    setModal('pagamento');
                  }}
                  aoConcederAjuste={() => {
                    setErroAcao(null);
                    setModal('ajuste');
                  }}
                  aoEstornar={(id) => {
                    setErroAcao(null);
                    setPagamentoEstornarId(id);
                    setModal('estornar');
                  }}
                />
              )}
              {aba === 'Financeiro' && !saldo && (
                <p className="py-6 text-[15px] text-secondary-500">
                  Dados financeiros indisponíveis.
                </p>
              )}
            </>
          )}
        </div>

        {modal === 'pagamento' && (
          <FormRegistrarPagamento
            aoSalvar={registrarPagamento}
            aoCancelar={() => {
              setModal('nenhum');
              setErroAcao(null);
            }}
            salvando={salvando}
            erroExterno={erroAcao}
            aoLimparErro={() => setErroAcao(null)}
          />
        )}
        {modal === 'ajuste' && saldo && (
          <FormConcederAjuste
            saldoCentavos={saldo.saldo_centavos}
            aoSalvar={concederAjuste}
            aoCancelar={() => {
              setModal('nenhum');
              setErroAcao(null);
            }}
            salvando={salvando}
            erroExterno={erroAcao}
            aoLimparErro={() => setErroAcao(null)}
          />
        )}
        {modal === 'estornar' && (
          <FormEstornarPagamento
            aoConfirmar={estornarPagamento}
            aoCancelar={() => {
              setModal('nenhum');
              setPagamentoEstornarId(null);
              setErroAcao(null);
            }}
            salvando={salvando}
            erroExterno={erroAcao}
            aoLimparErro={() => setErroAcao(null)}
          />
        )}
        {modal === 'cancelar' && (
          <ModalConfirmarCancelar
            nome={inscrito.participante}
            aoConfirmar={() => mudarStatus('cancelada')}
            aoCancelar={() => setModal('nenhum')}
            salvando={salvando}
          />
        )}
        {modal === 'cancelar-vagas' && (
          <FolhaCancelarInscricao
            nome={inscrito.participante}
            suplenteQueEntra={
              vagas?.temVaga ? (vagas?.primeiroSuplente?.nome ?? null) : null
            }
            aoConfirmar={cancelarComMotivo}
            aoFechar={() => {
              setModal('nenhum');
              setErroAcao(null);
            }}
            salvando={salvando}
            erro={erroAcao}
          />
        )}
        {modal === 'dar-vaga' && (
          <FolhaDarVaga
            nome={inscrito.participante}
            aoConfirmar={darVaga}
            aoFechar={() => {
              setModal('nenhum');
              setErroAcao(null);
            }}
            salvando={salvando}
            erro={erroAcao}
          />
        )}
      </div>

      {conviteAberto && (
        <FolhaConvite
          inscrito={inscrito}
          eventoNome={eventoNome}
          eventoInicio={eventoInicio}
          eventoLocal={eventoLocal ?? null}
          aoFechar={() => setConviteAberto(false)}
        />
      )}
    </div>
  );
}